import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OFFLINE_USER_KEY = "control_isp_offline_user";
const OFFLINE_CREDENTIALS_KEY = "control_isp_offline_credentials";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [isOfflineMode, setIsOfflineMode] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      // Tentar recuperar usuário offline se não houver token
      tryOfflineUser();
    }
  }, [token]);

  // Tentar carregar usuário do cache offline
  const tryOfflineUser = () => {
    if (!navigator.onLine) {
      const offlineUser = localStorage.getItem(OFFLINE_USER_KEY);
      if (offlineUser) {
        try {
          const userData = JSON.parse(offlineUser);
          setUser(userData);
          setIsOfflineMode(true);
          console.log("✅ Usuário carregado do cache offline");
        } catch (e) {
          console.error("Erro ao carregar usuário offline:", e);
        }
      }
    }
    setLoading(false);
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setIsOfflineMode(false);
      // Salvar usuário para uso offline
      localStorage.setItem(OFFLINE_USER_KEY, JSON.stringify(response.data));
    } catch (error) {
      console.error("Error fetching user:", error);
      
      // Se estiver offline, tentar usar dados em cache
      if (!navigator.onLine || error.code === "ERR_NETWORK") {
        const offlineUser = localStorage.getItem(OFFLINE_USER_KEY);
        if (offlineUser) {
          try {
            const userData = JSON.parse(offlineUser);
            setUser(userData);
            setIsOfflineMode(true);
            console.log("✅ Usando dados offline do usuário");
            setLoading(false);
            return;
          } catch (e) {
            console.error("Erro ao carregar usuário offline:", e);
          }
        }
      }
      
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    // Tentar login online primeiro
    if (navigator.onLine) {
      try {
        const response = await axios.post(`${API}/auth/login`, { email, password }, {
          timeout: 30000 // 30 segundos de timeout
        });
        const { token: newToken, user: userData } = response.data;
        
        // Salvar token e usuário
        localStorage.setItem("token", newToken);
        axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
        setToken(newToken);
        setUser(userData);
        setIsOfflineMode(false);
        
        // Salvar para uso offline (com hash da senha para verificação)
        const offlineCredentials = {
          email: email.toLowerCase(),
          passwordHash: btoa(password), // Codificação simples (em produção usar hash mais seguro)
          userData: userData
        };
        localStorage.setItem(OFFLINE_USER_KEY, JSON.stringify(userData));
        localStorage.setItem(OFFLINE_CREDENTIALS_KEY, JSON.stringify(offlineCredentials));
        
        return userData;
      } catch (error) {
        // Se falhar por falta de conexão, tentar login offline
        if (error.code === "ERR_NETWORK" || error.code === "ECONNABORTED" || !navigator.onLine) {
          return tryOfflineLogin(email, password);
        }
        // Se for erro 503, tentar login offline também
        if (error.response?.status === 503) {
          console.warn("Servidor indisponível (503), tentando login offline...");
          try {
            return tryOfflineLogin(email, password);
          } catch (offlineError) {
            // Se o login offline falhar, lançar o erro original
            throw error;
          }
        }
        throw error;
      }
    } else {
      // Modo offline - tentar login com credenciais salvas
      return tryOfflineLogin(email, password);
    }
  };

  // Login offline usando credenciais salvas
  const tryOfflineLogin = (email, password) => {
    const savedCredentials = localStorage.getItem(OFFLINE_CREDENTIALS_KEY);
    
    if (!savedCredentials) {
      throw new Error("Você está offline e nunca fez login neste dispositivo. Conecte-se à internet para fazer o primeiro login.");
    }
    
    try {
      const credentials = JSON.parse(savedCredentials);
      
      // Verificar email e senha
      if (credentials.email === email.toLowerCase() && credentials.passwordHash === btoa(password)) {
        const userData = credentials.userData;
        setUser(userData);
        setIsOfflineMode(true);
        
        // Restaurar token se existir
        const savedToken = localStorage.getItem("token");
        if (savedToken) {
          axios.defaults.headers.common["Authorization"] = `Bearer ${savedToken}`;
          setToken(savedToken);
        }
        
        console.log("✅ Login offline bem-sucedido");
        return userData;
      } else {
        throw new Error("Email ou senha incorretos");
      }
    } catch (e) {
      if (e.message === "Email ou senha incorretos") {
        throw e;
      }
      throw new Error("Erro ao verificar credenciais offline. Conecte-se à internet.");
    }
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    // Salvar para uso offline
    localStorage.setItem(OFFLINE_USER_KEY, JSON.stringify(userData));
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    // Não remover dados offline para permitir login offline futuro
    // localStorage.removeItem(OFFLINE_USER_KEY);
    // localStorage.removeItem(OFFLINE_CREDENTIALS_KEY);
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setIsOfflineMode(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, token, isOfflineMode }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
